import * as React from "react";
import { useSearchParams } from "react-router";
import { preload, useSWRConfig } from "swr";
import { CalendarPlus, ChevronLeft, ChevronRight, Inbox } from "lucide-react";
import type { AppointmentWithContext, Pagination as PageMeta } from "@taskiq/shared";
import { APPOINTMENT_STATUSES } from "@taskiq/shared";
import { Button } from "@/components/ui/Button";
import { ErrorState } from "@/components/ui/Feedback";
import { FilterBar } from "@/components/ui/FilterBar";
import { FilterSelect, type FilterOption } from "@/components/ui/FilterSelect";
import { IconButton } from "@/components/ui/IconButton";
import { PageHeader } from "@/components/ui/PageHeader";
import { Pagination } from "@/components/ui/Pagination";
import { Segmented } from "@/components/ui/Segmented";
import { AgendaList } from "@/components/schedule/AgendaList";
import { AppointmentPopover } from "@/components/schedule/AppointmentPopover";
import { EventChip } from "@/components/schedule/EventChip";
import { MonthGrid } from "@/components/schedule/MonthGrid";
import { WeekGrid } from "@/components/schedule/WeekGrid";
import { useAuth } from "@/lib/auth-context";
import { addDays, formatFullDay, todayIn, zonedRange, zoneOffset, type CalDate } from "@/lib/dates";
import { daysFrom, monthGrid, shiftMonth, startOfWeek, type Point } from "@/lib/schedule";
import { useTenancy } from "@/lib/tenancy";
import { AGENDA_PAGE_SIZE } from "@/lib/paging";
import { useSiteUsers } from "@/lib/scope-lists";
import { fetcher, useApi } from "@/lib/useApi";
import { cn } from "@/lib/cn";

/**
 * The schedule.
 *
 * ── It renders in the site's timezone, and says so ─────────────────────────
 *
 * Everywhere else in this console a timestamp is shown in the reader's own zone,
 * which is what `docs/01-conventions.md` asks for. A calendar cannot: nine
 * o'clock at a Texas yard means nine o'clock in Texas, and rendering that
 * instant for a supervisor in Hyderabad puts it at half past eight the previous
 * evening, in the wrong week. So every time on this screen is the site's, and
 * the zone is printed in the header rather than assumed. docs/decisions.md #25.
 *
 * ── One site at a time, like every other screen ────────────────────────────
 *
 * The site comes from the scope switcher and travels in the path. There is no
 * all-sites view: decisions #20 says a cross-site rollup is a different endpoint
 * under `/companies/:id/...` rather than a site parameter that accepts "all",
 * and a dispatcher covering three yards reads them one at a time.
 *
 * ── Day and week are a viewport; month and the agenda are documents ────────
 *
 * `page--fill` sizes day/week to the working area and clips it, so the only
 * thing that scrolls is the hour axis inside the grid. A calendar that scrolls
 * as a page loses its column headers the moment you look at the afternoon,
 * which is the one row that says which day you are reading - and the toolbar
 * that moves you to another week goes with it.
 *
 * **The agenda was never a calendar and month no longer squeezes into one
 * either.** The agenda is a list in reading order with no axis and nothing to
 * lose by scrolling. Month's rows held a floor height and scrolled internally
 * when a six-week month did not fit - which meant the exact same cell read a
 * different height depending on how many weeks the month had, on a screen tall
 * enough to fit five of them but not six. Both drop `page--fill`: a row keeps
 * one fixed height regardless of the month, and the page scrolls past it like
 * every other console page rather than nesting a second scrollbar inside a
 * fixed-height card.
 *
 * ── The three calendars are one request, not three ─────────────────────────
 *
 * **The fetch unit is the month grid, never the view.** `monthGrid(anchor)`
 * runs from the Monday of the week holding the 1st to the Sunday of the week
 * holding the last day, so it contains the anchor, the whole week around the
 * anchor, and the month - day, week and month are three slices of one response.
 * Switching between them changes no SWR key at all, which is why it is instant
 * and why the transition below has something to animate rather than a spinner.
 *
 * Keying on the view instead meant three cache entries per month holding
 * overlapping rows, and a refetch every time somebody pressed Week.
 *
 * The two neighbouring months are preloaded once the visible one settles, and
 * that covers every arrow on the toolbar: next-day from the 31st, next-week
 * from the last week, next-month, and the agenda's fortnight - all of them land
 * inside a chunk that is already in the cache. Three requests cover the whole
 * screen's navigation.
 *
 * ── The agenda is the one view that pages ──────────────────────────────────
 *
 * The three calendars ask for the whole window, because a month grid showing
 * page one of August is a month grid that is lying. The agenda has no such
 * obligation: it is a table, it reads top to bottom, and a fortnight at a busy
 * depot is a hundred rows the reader scrolls past to reach a footer. So it asks
 * for `AGENDA_PAGE_SIZE` rows of a fortnight from the anchor and puts a pager
 * under it - **server-side**, so the rows nobody is looking at are not fetched
 * rather than fetched and sliced away. It is the one view whose key is its own.
 *
 * The consequence is that a day's rows can straddle a page boundary. That is
 * fine and it is why the day heading repeats: the API orders by
 * `scheduled_start`, so a group is always contiguous, and page two opens with
 * the same day named again rather than with orphaned rows under no heading.
 *
 * ── There is no search box, and that is the design ─────────────────────────
 *
 * A text filter over a grid produces a calendar with holes in it: the same
 * screen that says "nothing on Thursday" when Thursday is full. Finding a job
 * by reference is a lookup and belongs on the visits table, which keeps its
 * search. docs/decisions.md #32.
 *
 * ── The unassigned tray is not filtered by the view ──────────────────────
 *
 * Its own request, fixed, with no `from`/`to` and none of the filter bar's
 * values on it. Work with nobody on it does not stop needing somebody because
 * the dispatcher paged forward a week, and a tray that emptied itself when you
 * changed the view was answering "what is unassigned *on screen*" - a question
 * nobody has. It is on every view for the same reason.
 */

type ViewName = "day" | "week" | "month" | "agenda";

const VIEWS: { value: ViewName; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "agenda", label: "Agenda" },
];

/**
 * How many unassigned appointments the tray holds before it says "and N more".
 *
 * A queue, not a report. Fifty is well past the point where the answer stops
 * being "assign these" and starts being "we are understaffed", which is not a
 * question a sidebar answers.
 */
const UNASSIGNED_PAGE_SIZE = 50;

interface ScheduleResponse {
  appointments: AppointmentWithContext[];
  pagination: PageMeta;
}

export function Schedule() {
  const { can } = useAuth();
  const tenancy = useTenancy();
  const { mutate: globalMutate } = useSWRConfig();

  const siteId = tenancy.activeSiteId;
  const companyId = tenancy.activeCompanyId;
  // The site's own zone, not the browser's. `homeSite` is the fallback while the
  // switcher's site is still resolving.
  const timezone = tenancy.homeSite?.timezone ?? "UTC";
  const zoneLabel = zoneOffset(timezone);

  // In the URL rather than in state alone: the dashboard's "View all" links
  // straight at the agenda, and a view somebody picked survives a reload and a
  // pasted link. `replace` so paging through a week does not fill the back
  // button with view changes.
  const [params, setParams] = useSearchParams();
  const view = parseView(params.get("view"));

  const setView = React.useCallback(
    (next: ViewName) => {
      setParams(
        (current) => {
          const updated = new URLSearchParams(current);
          updated.set("view", next);
          return updated;
        },
        { replace: true }
      );
    },
    [setParams]
  );
  const [anchor, setAnchor] = React.useState<CalDate>(() => todayIn(timezone));

  const [status, setStatus] = React.useState("");
  const [assignee, setAssignee] = React.useState("");
  const [agendaPage, setAgendaPage] = React.useState(1);

  const [dialog, setDialog] = React.useState<{
    open: boolean;
    appointment: AppointmentWithContext | null;
    initial: { date: CalDate; hour: number } | null;
    /** What the panel hangs off - the chip, hour cell, row or button clicked. */
    anchor: HTMLElement | null;
    /** And where in it, for the anchors that are the width of the screen. */
    point: Point | null;
  }>({ open: false, appointment: null, initial: null, anchor: null, point: null });

  const canWrite = can("appointment.write");

  // The month grid holding the anchor. Every calendar view is a slice of it -
  // see the header note - so this is both what the month view draws and what
  // all three of them are fetched as.
  const chunkDays = React.useMemo(() => monthGrid(anchor), [anchor]);

  // Which calendar days the current view *draws*. `MonthGrid` and `buildColumns`
  // both index by day key, so handing either of them the whole chunk and a
  // shorter day list is enough - nothing has to be filtered out first.
  const days = React.useMemo(() => {
    if (view === "day") return [anchor];
    if (view === "week") return daysFrom(startOfWeek(anchor), 7);
    if (view === "agenda") return daysFrom(anchor, 14);
    return chunkDays;
  }, [view, anchor, chunkDays]);

  // The filter half of every calendar key, so the chunk being drawn and the two
  // being preloaded cannot disagree about what they are filtered by.
  const filters = React.useMemo(() => {
    const parts = new URLSearchParams();
    if (status) parts.set("status", status);
    if (assignee === "__unassigned") parts.set("unassigned", "true");
    else if (assignee) parts.set("assigned_to", assignee);
    return parts.toString();
  }, [status, assignee]);

  // No `pageSize` on a chunk. A grid wants the whole window rather than a page
  // of it, and how many rows that is is the API's decision
  // (`MAX_APPOINTMENT_PAGE_SIZE`) rather than a number each client copies.
  const chunkKey = React.useCallback(
    (at: CalDate): string | null => {
      if (!siteId) return null;
      const grid = monthGrid(at);
      const span = zonedRange(grid[0]!, timezone, grid.length);
      return `/sites/${siteId}/appointments?from=${span.from}&to=${span.to}${filters ? `&${filters}` : ""}`;
    },
    [siteId, timezone, filters]
  );

  const agendaRange = React.useMemo(() => zonedRange(anchor, timezone, 14), [anchor, timezone]);

  const agendaKey =
    siteId &&
    `/sites/${siteId}/appointments?from=${agendaRange.from}&to=${agendaRange.to}` +
      `&page=${agendaPage}&pageSize=${AGENDA_PAGE_SIZE}${filters ? `&${filters}` : ""}`;

  const activeKey = view === "agenda" ? agendaKey || null : chunkKey(anchor);

  const { data, error, isLoading, mutate } = useApi<ScheduleResponse>(activeKey);

  /**
   * Warm the months either side, once the one on screen has arrived.
   *
   * `preload` rather than a second `useApi`: this fills SWR's cache without
   * subscribing, so a neighbour resolving does not re-render a screen nobody is
   * looking at it from. Gated on `data` so it never competes for a connection
   * with the request the reader is actually waiting on.
   */
  React.useEffect(() => {
    if (!data) return;
    for (const key of [chunkKey(shiftMonth(anchor, -1)), chunkKey(shiftMonth(anchor, 1))]) {
      if (key) void preload(key, fetcher);
    }
  }, [data, anchor, chunkKey]);

  // Narrowing moves the last page underneath you - page 4 of a fortnight that
  // now holds six appointments is an empty table reading as "nothing scheduled".
  // The range is in here too, because paging the anchor forward is a different
  // fortnight rather than more of this one.
  React.useEffect(() => {
    setAgendaPage(1);
  }, [status, assignee, agendaRange.from, siteId]);

  // The dispatcher's inbox, and deliberately not a slice of `data`. No date
  // bounds, no filters, sorted oldest first by the API - the appointment that
  // has gone longest without somebody on it is the one that needs somebody
  // most, and it is invisible if the tray only ever holds what the current view
  // happens to cover.
  //
  // `status=scheduled` rather than filtering client-side: cancelled work needs
  // nobody, and `in_progress` or `completed` without an assignee means somebody
  // turned up anyway. None of the three belong in a queue of things to hand out.
  const unassignedList = useApi<ScheduleResponse>(
    siteId
      ? `/sites/${siteId}/appointments?unassigned=true&status=scheduled&pageSize=${UNASSIGNED_PAGE_SIZE}`
      : null
  );

  const people = useSiteUsers(siteId);

  const appointments = data?.appointments ?? [];

  const unassigned = unassignedList.data?.appointments ?? [];
  // The badge counts everything, not what fits. A queue that says "12" while
  // holding fifty is worse than one that admits it is showing the first fifty.
  const unassignedTotal = unassignedList.data?.pagination.total ?? unassigned.length;

  // The API returned fewer rows than exist. Said out loud rather than silently
  // truncated, because a calendar quietly missing its last few rows is worse
  // than one admitting it is full.
  //
  // Never on the agenda: there the shortfall is the pager doing its job, and a
  // banner saying "showing 10 of 240" above a footer saying "showing 1-10 of
  // 240" is the same sentence twice, one of them phrased as a problem.
  const truncated = data && view !== "agenda" ? data.pagination.total > appointments.length : false;

  const activeFilters = [status, assignee].filter(Boolean).length;

  const personOptions: FilterOption[] = [
    { value: "", label: "Anyone" },
    { value: "__unassigned", label: "Unassigned only" },
    ...(people.data?.users ?? []).map((person) => ({
      value: person.id,
      label: person.full_name ?? person.email,
      hint: person.email,
    })),
  ];

  function step(direction: 1 | -1) {
    if (view === "day") setAnchor((d) => addDays(d, direction));
    else if (view === "month") setAnchor((d) => shiftMonth(d, direction));
    else setAnchor((d) => addDays(d, direction * (view === "agenda" ? 14 : 7)));
  }

  function openNew(
    anchor: HTMLElement,
    initial?: { date: CalDate; hour: number },
    point?: Point | null
  ) {
    setDialog({
      open: true,
      appointment: null,
      initial: initial ?? null,
      anchor,
      point: point ?? null,
    });
  }

  function openExisting(
    appointment: AppointmentWithContext,
    anchor: HTMLElement,
    point?: Point | null
  ) {
    setDialog({ open: true, appointment, initial: null, anchor, point: point ?? null });
  }

  // Only the flag. The panel fades rather than vanishing - see `CalendarPopover`
  // - so clearing the record here would swap the appointment being read for an
  // empty "New appointment" form for the length of the fade. Nothing reads any
  // of the rest while `open` is false, and the next open replaces all of it.
  function closeDialog() {
    setDialog((current) => ({ ...current, open: false }));
  }

  return (
    <div className={cn("page", (view === "day" || view === "week") && "page--fill")}>
      <PageHeader
        title="Schedule"
        description={
          <>
            Planned work at {tenancy.homeSite?.name ?? "this site"}.{" "}
            {/* The zone, named. A calendar that renders in a zone it does not
                state is one you have to take on trust. */}
            <span className="subtle">{zoneLabel}</span>
          </>
        }
      />

      <div className="sched-bar">
        <div className="sched-nav">
          <IconButton size="sm" label="Previous" onClick={() => step(-1)}>
            <ChevronLeft />
          </IconButton>
          <Button variant="secondary" size="sm" onClick={() => setAnchor(todayIn(timezone))}>
            Today
          </Button>
          <IconButton size="sm" label="Next" onClick={() => step(1)}>
            <ChevronRight />
          </IconButton>
          <p className="sched-range">{rangeLabel(view, days)}</p>
        </div>

        <div className="sched-bar__filters">
          <FilterBar
            activeCount={activeFilters}
            onClear={() => {
              setStatus("");
              setAssignee("");
            }}
          >
            <FilterSelect
              label="Filter by status"
              value={status}
              active={status !== ""}
              options={[
                { value: "", label: "Any status" },
                ...APPOINTMENT_STATUSES.map((value) => ({
                  value,
                  label: value === "in_progress" ? "In progress" : capitalise(value),
                })),
              ]}
              onChange={setStatus}
            />
            <FilterSelect
              label="Filter by assignee"
              value={assignee}
              active={assignee !== ""}
              searchable
              options={personOptions}
              onChange={setAssignee}
            />
          </FilterBar>
        </div>

        {/* On the toolbar rather than in the page header: the header is a title
            row, and these change what the grid below shows - one row of
            controls instead of two, and a calendar that starts higher up. */}
        <div className="sched-bar__actions">
          <Segmented label="Calendar view" value={view} options={VIEWS} onChange={setView} />

          {canWrite && (
            <Button
              size="sm"
              icon={<CalendarPlus />}
              onClick={(event) => openNew(event.currentTarget)}
            >
              New appointment
            </Button>
          )}
        </div>
      </div>

      {error && <ErrorState error={error} onRetry={() => void mutate()} />}

      {truncated && (
        <p className="notice" role="status">
          Showing {appointments.length} of {data?.pagination.total} appointments in this range.
          Narrow the filters or pick a shorter view to see the rest.
        </p>
      )}

      {!(error && !data) && (
        <div className="sched-body">
          {/* Keyed on the view so React remounts rather than reconciles - each
              view keeps its own scroll position instead of inheriting the
              previous one's. */}
          <div key={view} className="sched-main">
            {view === "agenda" ? (
              <>
                <AgendaList
                  appointments={appointments}
                  timezone={timezone}
                  loading={isLoading}
                  onOpen={openExisting}
                  surface="flow"
                />
                {data && (
                  <Pagination
                    pagination={data.pagination}
                    onPageChange={setAgendaPage}
                    label="appointments"
                  />
                )}
              </>
            ) : view === "month" ? (
              <MonthGrid
                days={days}
                month={anchor.m}
                appointments={appointments}
                timezone={timezone}
                onOpen={openExisting}
                onSelectDay={(date) => {
                  setAnchor(date);
                  setView("day");
                }}
              />
            ) : (
              <WeekGrid
                days={days}
                appointments={appointments}
                timezone={timezone}
                onOpen={openExisting}
                onCreateAt={
                  canWrite
                    ? (date, hour, element, point) => openNew(element, { date, hour }, point)
                    : undefined
                }
              />
            )}
          </div>

          {/* The dispatcher's inbox. Cheapest genuinely useful thing on this
              screen - it is a filtered list - and the one that answers "what
              still needs somebody on it" without reading every column.

              Deliberately a short fixed box that scrolls rather than a column
              matching the calendar's height: it is a queue you work off the top
              of, and stretching it to 900px made the two or three rows in it
              look like the page's main content. */}
          <aside className="sched-tray" aria-label="Unassigned appointments">
            {/* Outside the scroll, so the count stays in view while the list
                under it moves. */}
            <h2 className="type-label sched-tray__title">
              <Inbox aria-hidden /> Unassigned
              <span className="sched-tray__count tnum">{unassignedTotal}</span>
            </h2>

            <div className="sched-tray__scroll">
              {unassignedList.isLoading ? (
                <p className="sched-tray__empty">Loading</p>
              ) : unassigned.length === 0 ? (
                <p className="sched-tray__empty">Every scheduled appointment has somebody on it.</p>
              ) : (
                <>
                  <ul className="sched-tray__list">
                    {unassigned.map((appointment) => (
                      <li key={appointment.id}>
                        <EventChip
                          appointment={appointment}
                          timezone={timezone}
                          onOpen={openExisting}
                          variant="tray"
                        />
                      </li>
                    ))}
                  </ul>

                  {unassignedTotal > unassigned.length && (
                    <p className="sched-tray__empty">
                      {unassignedTotal - unassigned.length} more not shown.
                    </p>
                  )}
                </>
              )}
            </div>
          </aside>
        </div>
      )}

      {siteId && (
        <AppointmentPopover
          open={dialog.open}
          anchor={dialog.anchor}
          point={dialog.point}
          siteId={siteId}
          companyId={companyId}
          timezone={timezone}
          zoneLabel={zoneLabel}
          appointment={dialog.appointment}
          initial={dialog.initial}
          canWrite={canWrite}
          onClose={closeDialog}
          onSaved={() => {
            closeDialog();
            // Every appointment key at this site, not just the one on screen.
            // Rescheduling moves a row between months and reassigning moves it
            // into or out of the tray, so `mutate()` on the visible chunk would
            // leave the two preloaded neighbours and the tray holding a row
            // that is no longer theirs - and SWR would serve that stale copy the
            // moment somebody pressed an arrow.
            void globalMutate(
              (key) => typeof key === "string" && key.startsWith(`/sites/${siteId}/appointments`)
            );
          }}
        />
      )}
    </div>
  );
}

/** The `?view=` parameter, or the week. An unknown value is a typo, not a view. */
function parseView(value: string | null): ViewName {
  return VIEWS.some((option) => option.value === value) ? (value as ViewName) : "week";
}

/* ────────────────────────────── helpers ────────────────────────────── */

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function rangeLabel(view: ViewName, days: CalDate[]): string {
  const first = days[0]!;
  const last = days[days.length - 1]!;

  if (view === "day") return formatFullDay(new Date(first.y, first.m - 1, first.d));
  if (view === "month") {
    // The middle of the grid, not its first day - which belongs to the previous
    // month whenever the first falls mid-week.
    const middle = days[Math.floor(days.length / 2)]!;
    return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(
      new Date(middle.y, middle.m - 1, middle.d)
    );
  }

  const from = new Date(first.y, first.m - 1, first.d);
  const to = new Date(last.y, last.m - 1, last.d);
  const sameMonth = first.m === last.m && first.y === last.y;

  const fromLabel = new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    ...(sameMonth ? {} : { month: "short" }),
  }).format(from);
  const toLabel = new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(to);

  return `${fromLabel} - ${toLabel}`;
}
